import { get, post, del } from './api';
import { Chat, ChatCreate, ResearchChatCreate, Message, MessageCreate, ToolResult, ChatType } from '../types/chat';
import { apiEndpointManager } from './apiEndpointManager';

// Get all chats for the current user
export const getUserChats = async (): Promise<Chat[]> => {
  return get<Chat[]>('/chat');
};

// Create a new chat
export const createChat = async (data: ChatCreate): Promise<Chat> => {
  return post<Chat>('/chat', data);
};

// Create a new research chat
export const createResearchChat = async (data: ResearchChatCreate): Promise<Chat> => {
  return post<Chat>('/chat/research', data);
};

// Create a new chat with report context
export const createChatWithReportContext = async (
  title: string, 
  reportContext: any, 
  initialMessage?: string
): Promise<Chat> => {
  const chatData: ChatCreate = {
    name: title,
    type: ChatType.GENERAL
  };
  return post<Chat>('/chat', chatData);
};

// Get a chat by ID with messages
export const getChat = async (chatId: string): Promise<Chat> => {
  return get<Chat>(`/chat/${chatId}`);
};

// Send a message to a chat (non-streaming)
export const sendMessage = async (chatId: string, data: MessageCreate): Promise<Message> => {
  return post<Message>(`/chat/${chatId}/messages`, data);
};

// Send a message to a chat with streaming response
export const sendStreamingMessage = async (
  chatId: string, 
  message: string, 
  researchContext: any = null
): Promise<Response> => {
  try {
    // Import Firebase auth
    const { auth } = await import('../firebase/config');
    
    // Get current user
    const user = auth.currentUser;
    if (!user) {
      console.error("No current user found in Firebase auth");
      throw new Error('Authentication required');
    }
    
    
    // Get token from Firebase
    const token = await user.getIdToken();
    
    const requestBody = {
      message,
      chat_id: chatId,
      research_context: researchContext
    };
    
    // Get the current API URL from the endpoint manager
    const apiBaseUrl = apiEndpointManager.getCurrentUrl();
    const streamingEndpoint = `${apiBaseUrl}/streaming-chat/stream`;
    
    
    
    // Make the request with extended timeout for car analysis
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 420000); // 7 minute timeout for car analysis
    
    try {
      const response = await fetch(streamingEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
    
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout - car analysis is taking longer than expected');
      }
      throw error;
    }
  } catch (error) {
    console.error("Error in sendStreamingMessage:", error);
    throw error;
  }
};


// Send a message to a chat with streaming but collect all results (fallback)
export const sendStreamingMessageCollected = async (
  chatId: string, 
  message: string, 
  researchContext: any = null
): Promise<{content: string, tool_results: ToolResult[]}> => {
  // Use the post function which already handles authentication
  return post<{content: string, tool_results: ToolResult[]}>(`/streaming-chat/message`, {
    message,
    chat_id: chatId,
    research_context: researchContext
  });
};

// Delete a chat
export const deleteChat = async (chatId: string): Promise<{ message: string }> => {
  return del<{ message: string }>(`/chat/${chatId}`);
};
